import apiTranslations from './en.api.ts';
import primeVueTranslations from './en.primevue.ts';
import {
  generalTerm,
  generalTermFilter,
} from './en.shared.ts';

import npm from '@/generated/npm/locales/en.npm.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  EAttributeLabel,
} from '@/types/attribute-type.ts';
import {
  EWeekday,
} from '@/types/calendar.ts';
import {
  EMonth,
} from '@/types/date.ts';
import {
  ETimeseriesMeaning,
} from '@/types/openapi-type.ts';
import {
  EVisualisation,
} from '@/types/performance-dashboard.ts';
import {
  TUnitSystem,
} from '@/types/unit-of-measurement.ts';
import {
  getComponentScopedTranslationMap,
} from '@/utils/i18n.ts';

const Visualisation: Record<EVisualisation, string> = {
  [EVisualisation.CLUSTERED_STACKED_BAR_CHART]: 'Clustered stacked bar chart',
  [EVisualisation.PARETO_CHART]: 'Pareto chart',
  [EVisualisation.STACKED_BAR_CHART]: 'Stacked bar chart',
  [EVisualisation.STACKED_POLAR_CHART]: 'Stacked polar chart',
  [EVisualisation.TABLE]: 'Table',
};

const unitSystem: Record<TUnitSystem, string> = {
  metric: 'Metric system',
  imperial: 'Imperial system',
};

export default {
  generalTermFilter,
  generalTerm,
  api: apiTranslations,
  primeVue: primeVueTranslations,
  npm,
  actions: {
    clear: 'Clear',
    accept: 'Accept',
    login: 'Login',
    reset: 'Reset',
    search: 'Search',
    select: 'Select',
    unlink: 'Unlink',
  },
  alerts: {
    saveFailed: 'Save failed',
    deleteSuccess: 'Deleted successfully',
    valueAboveMax: 'Value should be below',
  },
  assetManagement: {
    confirmResurrection: {
      header: 'Reactivate "{item}"?',
      message: 'The retired asset returns to operation.',
    },
    confirmDelete: {
      header: 'Delete or retire "{item}"?',
      message: 'Assets with data, also in their child assets, are retired instead and can be reactivated later.',
    },
    includeRetiredAssets: 'Show retired',
    resurrectAsset: 'Reactivate',
  },
  buttons: {
    acknowledge: 'Acknowledge',
    analogInputs: 'Analog inputs',
    cancel: 'Cancel',
    digitalInputs: 'Digital inputs',
    editOverview: 'Edit Overview',
    loadList: 'Load list',
    upload: 'Upload',
  },
  calendar: {
    occurs: 'Occurs',
    on: 'On',
    startingOn: 'starting on',
  },
  charts: {
    position: {
      left: 'Left',
      right: 'Right',
    },
    newAxis: 'New axis',
    noAlarms: 'No alarms',
  },
  fullscreen: {
    exitFullscreen: 'Exit fullscreen',
    fullscreen: 'Fullscreen',
  },
  measurementUnit: {
    noGroup: 'Other',
  },
  attributeUnit: {
    [EAttributeLabel.WEIGHT]: 'Weight',
    [EAttributeLabel.LENGTH]: 'Length',
    [EAttributeLabel.DIAMETER]: 'Diameter',
    [EAttributeLabel.SURFACE_HARDNESS]: 'Surface hardness',
    [EAttributeLabel.SURFACE_HARDNESS_MIN]: 'Minimum surface hardness',
    [EAttributeLabel.SURFACE_HARDNESS_MAX]: 'Maximum surface hardness',
    [EAttributeLabel.CORE_HARDNESS]: 'Core hardness',
    [EAttributeLabel.CORE_HARDNESS_MIN]: 'Minimum core hardness',
    [EAttributeLabel.CORE_HARDNESS_MAX]: 'Maximum core hardness',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MIN]: 'ECD min',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_MAX]: 'ECD max',
    [EAttributeLabel.EFFECTIVE_CASE_DEPTH_HARDNESS]: 'ECD hardness',
    neuAttributePlaceholder: 'Attribute Label',
  },
  equipmentGasName: {
    [ETimeseriesMeaning.AIR_FLOW_SETPOINT]: 'Air',
    [ETimeseriesMeaning.HYDROGEN_H2_FLOW_SETPOINT]: 'Hydrogen',
    [ETimeseriesMeaning.NITROGEN_FLOW_SETPOINT]: 'Nitrogen',
    [ETimeseriesMeaning.OXYGEN_O2_FLOW_SETPOINT]: 'Oxygen',
    [ETimeseriesMeaning.AMMONIA_NH3_FLOW_SETPOINT]: 'Ammonia',
    [ETimeseriesMeaning.DISSOCIATED_AMMONIA_NH3_FLOW_SETPOINT]: 'Dissociated Ammonia',
    [ETimeseriesMeaning.CARBON_DIOXIDE_CO2_FLOW_SETPOINT]: 'CO2',
    [ETimeseriesMeaning.CARBON_MONOXIDE_CO_FLOW_SETPOINT]: 'CO',
    [ETimeseriesMeaning.METHANE_CH4_FLOW_SETPOINT]: 'Methane',
    [ETimeseriesMeaning.PROPANE_C3H8_FLOW_SETPOINT]: 'Propane',
    [ETimeseriesMeaning.NITROUS_OXIDE_N2O_FLOW_SETPOINT]: 'N2O',
    [ETimeseriesMeaning.METHANOL_CH3OH_FLOW_SETPOINT]: 'Methanol',
    [ETimeseriesMeaning.ACETYLENE_C2H2_FLOW_SETPOINT]: 'Acetylene',
    [ETimeseriesMeaning.ARGON_FLOW_SETPOINT]: 'Argon',
    [ETimeseriesMeaning.ENDO_GAS_NATURAL_FLOW_SETPOINT]: 'Endo Gas Natural',
    [ETimeseriesMeaning.ENDO_GAS_PROPANE_FLOW_SETPOINT]: 'Endo Gas Propane',
    [ETimeseriesMeaning.ETHENE_C2H4_FLOW_SETPOINT]: 'Ethene',
    [ETimeseriesMeaning.HELIUM_FLOW_SETPOINT]: 'Helium',
    [ETimeseriesMeaning.NATURAL_GAS_FLOW_SETPOINT]: 'Natural Gas',
    [ETimeseriesMeaning.WATER_FLOW_SETPOINT]: 'Water',
  },
  // single words or small phrases WITHOUT CONTEXT
  common: {
    min: 'Minimum',
    max: 'Maximum',
    emptyTable: 'No matching data found.',
    attribute: 'Attribute',
    revealItem: 'Reveal {item}',
    hideItem: 'Hide {item}',
    numberedItem: '{item} {number}',
    valueRange: 'Value range',
    all: 'All',
    user: 'User',
    date: 'Date',
    actions: 'Actions',
    yes: 'Yes',
    no: 'No',
    clear: 'Clear',
    delete: 'Delete',
    discard: 'Discard',
    keepEditing: 'Keep editing',
    save: 'Save',
    duration: 'Duration',
    download: 'Download {item}',
    comment: 'Comment',
    print: 'Print',
    chart: 'Chart',
    description: generalTerm.description,
    status: 'Status',
    assignee: 'Assignee',
    temperature: 'Temperature',
    name: 'Name',
    filters: 'Filters',
    overview: 'Overview',
    type: 'Type',
    set: 'Set',
    username: 'Username',
    password: 'Password',
    include: 'Include',
    exclude: 'Exclude',
    custom: 'Custom',
    loading: 'Loading',
    retired: 'retired',
    carbon: 'Carbon',
    enterForNewRow: 'Press "Enter" to add new row',
    now: 'Now',
    note: 'Note',
    target: 'Target',
    source: 'Source',
    skip: 'Skip',
    reject: 'Reject',
    tag: 'Tag | Tags',
    withoutTag: 'Without tag',
    maintenance: 'Maintenance',
    filter: 'Filter | Filters',
    navigation: 'Navigation',
    calendar: 'Calendar',
    noAccessToAction: 'You do not have access to this action.',
    tiny: 'Tiny',
    large: 'Large',
    size: 'Size',
    appearance: 'Appearance',
    notification: 'Notification | Notifications',
    meaning: 'Meaning| Meanings',
    result: 'Result',
    toggle: 'Toggle',
    statistics: 'Statistics',
    rating: 'Rating',
    showTable: 'Show table',
    showChart: 'Show chart',
    configuration: 'Configuration',
    passed: 'Passed',
    failed: 'Failed',
    section: 'Section',
    capacity: 'capacity | Capacities',
    gas: 'Gas | Gases',
    capability: 'Capability | Capabilities',
    certificate: 'Certificate | Certificates',
    model: 'Model | Models',
    createItem: 'Create {item}',
    legend: 'Legend',
    linkItem: 'Link {item}',
    today: 'Today',
    earlier: 'Earlier',
    addItem: 'Add {item}',
    image: 'Image',
    file: 'File',
    pasteFromClipboard: 'Paste from clipboard',
    noItemInClipboard: 'No {item} in clipboard',
    assetNavigation: 'Asset Navigation',
    noRecipeSelected: 'No recipe selected',
    addAssessment: 'Add assessment',
    addRow: 'Add row',
    setAll: 'Set all',
    resetAll: 'Reset all',
    unlock: 'Unlock',
    notAvailable: 'N/A',
    stop: 'Stop',
    loadItem: 'Load {item}',
    add: 'Add',
    valueAtTime: '{value} at {time}',
    timeline: 'Timeline',
    Ferrite: 'Ferrite',
    Austenite: 'Austenite',
    Martensite: 'Martensite',
    labelInUnit: '{label} in {unit}',
    default: 'Default',
    settings: 'Settings',
    version: 'Version',
    logout: 'Logout',
    confirm: 'Confirm',
    edit: 'Edit',
    start: 'Start',
    confirmUnlink: {
      header: 'Unlink "{item}"?',
      message: 'Both entries are kept and can be linked again.',
    },
    confirmDelete: {
      header: 'Delete "{item}"?',
      message: 'This can\'t be undone.',
    },
    confirmRemove: {
      header: 'Remove "{item}"?',
    },
    takesEffectOnSave: 'The change takes effect when you save.',
    confirmSaveChanges: {
      header: 'Save changes to "{item}"?',
      message: 'This overwrites the saved version.',
    },
    confirmChangeValue: {
      header: 'Change value to "{value}"?',
      message: 'The current value is "{currentValue}".',
    },
    remove: 'Remove',
    selectAll: 'Select all',
    selectItem: 'Select {item}',
    deselectAll: 'Deselect all',
    value: 'Value | Values',
    validate: 'Validate',
    selectEquipment: 'Please select an Asset from the Asset Navigation on the left to see Details',
    required: 'Required',
    new: 'New',
    calendarWeekAbbreviation: 'CW',
    unlink: 'Unlink',
    other: 'Other',
    cancel: 'Cancel',
    error: 'Error',
    schedule: 'Schedule',
    always: 'Always',
    asset: 'Asset',
    assets: 'Assets',
    severityLevels: 'Severity levels',
    history: 'History',
    restore: 'Restore',
    errorMessage: {
      errorSignalLoading: 'Error loading {displayName}',
      noAccess: '{entity} does not have "{accessType}" access to {item}',
      default: 'Something went wrong, please reload your page. If error persists, please contact support.',
      pt9800: {
        AxiosError401: 'Usermapping not done or invalid',
        AxiosError401Item: 'Usermapping not done or invalid for {item}',
        AxiosError403: 'User does not have the required permissions to access Protherm data',
        AxiosError403Item: 'User does not have the required permissions to access Protherm data for {item}',
      },
    },
    fileGeneration: {
      summary: 'Generating file',
      detail: 'The generation of a file may take a while. Please keep the browser window open. This message will close once the request has completed.',
    },
    update: 'Update',
    create: 'Create',
    createCopy: 'Create copy',
    deleteItem: 'Delete {item}',
    editItem: 'Edit {item}',
    saveItem: 'Save {item}',
    saveAsNew: 'Save as ...',
    export: 'Export',
    import: 'Import',
    search: 'Search',
    selected: 'Selected',
    back: 'Back',
    forward: 'Forward',
    accept: 'Accept',
    submit: 'Submit',
    success: 'Success',
    close: 'Close',
    week: {
      [EWeekday.MONDAY]: apiTranslations.Weekday.MONDAY,
      [EWeekday.TUESDAY]: apiTranslations.Weekday.TUESDAY,
      [EWeekday.WEDNESDAY]: apiTranslations.Weekday.WEDNESDAY,
      [EWeekday.THURSDAY]: apiTranslations.Weekday.THURSDAY,
      [EWeekday.FRIDAY]: apiTranslations.Weekday.FRIDAY,
      [EWeekday.SATURDAY]: apiTranslations.Weekday.SATURDAY,
      [EWeekday.SUNDAY]: apiTranslations.Weekday.SUNDAY,
    },
    month: {
      [EMonth.JANUARY]: {
        long: 'January',
        short: 'Jan',
      },
      [EMonth.FEBRUARY]: {
        long: 'February',
        short: 'Feb',
      },
      [EMonth.MARCH]: {
        long: 'March',
        short: 'Mar',
      },
      [EMonth.APRIL]: {
        long: 'April',
        short: 'Apr',
      },
      [EMonth.MAY]: {
        long: 'May',
        short: 'May',
      },
      [EMonth.JUNE]: {
        long: 'June',
        short: 'Jun',
      },
      [EMonth.JULY]: {
        long: 'July',
        short: 'Jul',
      },
      [EMonth.AUGUST]: {
        long: 'August',
        short: 'Aug',
      },
      [EMonth.SEPTEMBER]: {
        long: 'September',
        short: 'Sep',
      },
      [EMonth.OCTOBER]: {
        long: 'October',
        short: 'Oct',
      },
      [EMonth.NOVEMBER]: {
        long: 'November',
        short: 'Nov',
      },
      [EMonth.DECEMBER]: {
        long: 'December',
        short: 'Dec',
      },
    },
    timeAbbreviation: {
      days: 'days',
      hours: 'hr',
      minutes: 'min',
      months: 'mth',
      weeks: 'wk',
      years: 'yr.',
    },
    reset: 'Reset',
    none: 'None',
    copy: 'Copy',
    copyNoun: 'Copy',
    time: {
      second: 'Second | Seconds',
      minute: 'Minute | Minutes',
      hour: 'Hour | Hours',
      day: 'Day | Days',
      weekday: 'Weekday | Weekdays',
      week: 'Week | Weeks',
      month: 'Month | Months',
      quarter: 'Quarter | Quarters',
      year: 'Year | Years',
    },
    task: 'Task | Tasks',
    read: 'Read',
    write: 'Write',
  },
  component: {
    accordionAudit: {
      envelopeNamePlaceholder: 'Envelope name',
    },
    alarmView: {
      header: 'Alarms',
      empty: 'No alarms found',
    },
    appAndAssetPermissionTree: {
      appPermission: 'App permissions',
      assetPermission: 'Asset permissions',
    },
    counterActionModal: {
      create: 'Create counter action',
      update: 'Update counter action',
    },
    counterActionTable: {
      create: 'Create counter action',
      confirmDelete: 'Delete counter action "{counterActionName}"?',
      confirmUnlink: 'Unlink root cause "{rootCauseName}" from counter action "{counterActionName}"?',
    },
    dashboardPanel: {
      add: 'Add panel',
      defaultTitle: 'My Panel',
    },
    furnaceViewRecipe: {
      stopConfirm: {
        header: 'Stop recipe "{0}"?',
        message: 'The recipe stops right away.',
      },
      startConfirm: {
        header: 'Start recipe "{0}"?',
        message: 'The recipe starts right away.',
      },
      goToConfirm: {
        message: 'The recipe continues with this step right away.',
      },
    },
    expressionPreviewModal: {
      title: 'New virtual signal',
    },
    equipmentDocumentUploadModal: {
      headline: 'Upload documents',
      categoryTitle: 'Category',
      descriptionTitle: 'Description',
      uploadFilesTitle: 'File',
      errorMessage: 'File already exists',
    },
    loadModal: {
      startingLoad: 'Job is starting...',
      confirmPartHint: 'Please confirm that all parts are loaded',
      confirmStart: 'Confirm the start of the job.',
      loadIdentifier: generalTerm.loadIdentifier,
      createOrUpdate: 'Schedule job',
      description: 'Schedule your next load by adding your',
      addTraveller: 'Add new traveller',
      emptyPartsTable: 'No parts added',
      partNotFound: 'Part with ID "{id}" was not found',
      partDoNotMatch: 'Not all parts match the recipe',
      readyToScan: 'Ready to scan',
    },
    materialSelect: {
      placeholder: 'Select material',
    },
    materialGroupTable: {
      tableHeader: 'Material groups',
    },
    maintenanceCalendarJobModal: {
      titleCreate: 'Create maintenance job',
      titleEdit: 'Edit maintenance job',
      createTask: 'Create task',
    },
    observationView: {
      header: 'Observations',
    },
    observationTypeTable: {
      unlinkConfirm: 'Unlink root cause "{rootCauseName}" from observation type "{observationTypeName}"?',
    },
    PartAttribute: {
      headline: 'Part settings',
      subheadline: 'The attributes defined in this table will be requested to be filled during creation/modification of parts.',
    },
    productionLogsTable: {
      tab: {
        table: 'Table',
        chart: 'Chart',
      },
    },
    qualityForm: {
      jobSelection: 'Load selection',
      partSelection: 'Part selection',
    },
    rootCauseLinkModal: {
      title: 'Link root cause',
    },
    rootCauseModal: {
      create: 'Create root cause',
      update: 'Update root cause',
    },
    rootCauseGroupModal: {
      create: 'Create root cause group',
      update: 'Update root cause group',
    },
    rootCauseTable: {
      create: 'Create root cause',
      confirmDelete: 'Delete root cause "{rootCauseName}"?',
      confirmUnlink: 'Unlink counter action "{counterActionName}" from root cause "{rootCauseName}"?',
    },
    rootCauseGroupTable: {
      create: 'Create root cause group',
      confirmDelete: 'Delete root cause group "{rootCauseGroupName}"?',
      confirmUnlink: 'Unlink root cause "{rootCauseName}" from root cause group "{rootCauseGroupName}"?',
    },
    TableJobStages: {
      header: 'Recipe stages',
    },
    treeSidebarTemplate: {
      defaultTitle: 'Asset Navigation',
    },
    userUpdateModal: {
      tab: {
        permissionList: 'Permissions',
        controllerUserMapping: 'Controller user mapping',
      },
      permissionList: 'Permission list',
      groupList: 'Group list',
      selectPT9800: 'Select Protherm user',
      enterCredentials: 'Enter credentials',
    },
  },
  details: {
    images: 'Images',
  },
  errorMessages: {
    error: 'Error',
    noEquipmentSelected: 'Please select an Asset from the Asset Navigation on the left to see Details',
    outOfRangeTemperature: 'Temperature is out of range: -200°C - 850°C (-328°F - 1562°F). Please adjust RG or RL value',
  },
  forms: {
    credentials: 'Credentials',
    persistCredentials: 'Persist credentials',
  },
  global: {
    workplace: 'Workplace',
  },
  images: {
    noImages: 'No Images',
  },
  inputLabels: {
    from: 'from',
    to: 'to',
  },
  materialComposition: {
    min: {
      label: 'Min',
      description: 'Analysis with the smallest material properties.',
    },
    max: {
      label: 'Max',
      description: 'Analysis with the largest material properties.',
    },
    avg: {
      label: 'Average',
      description: 'Analysis with average material properties.',
    },
    comp: {
      label: 'Mean analysis',
      description: 'Average analysis based on weight percentages.',
    },
  },
  modifyMenu: {
    addNew: 'New {0}',
    edit: 'Edit',
    saveAs: 'Save As',
  },
  page: {
    anomalyDetection: {
      noObservations: 'No observations found',
    },
    certification: {
      title: 'Audit & Quality',
      auditConfiguration: {
        deleteSuccess: 'Specification was successfully deleted.',
      },
    },
    openSourceLicenses: {
      title: 'Open Source Licenses',
    },
    liveApp: {
      equipment: {
        noJobs: 'No jobs found',
      },
    },
    notification: {
      title: 'Notifications',
    },
    userManagement: {
      manageRole: 'Manage roles',
      headline: 'Users ({0})',
      customPermissions: 'Custom permissions',
      searchPlaceholder: {
        name: 'Search by name',
        email: 'Search by e-mail',
        role: 'Search by role',
      },
      showDeletedUsers: 'Show deleted users',
      restoreUser: 'Restore user',
      confirmRestoreUser: {
        header: 'Restore "{displayName}"?',
        message: 'The user can log in to Qmulus again.',
      },
      confirmDeleteUserMessage: 'Deleted users can be restored.',
      deleted: 'Deleted',
    },
    roleManagement: {
      roles: 'Roles',
      createRole: 'Create role',
    },
    home: {
      title: 'Home',
      welcomeMorning: 'Good morning, {firstName}.',
      welcomeAfternoon: 'Good afternoon, {firstName}.',
      welcomeEvening: 'Good evening, {firstName}.',
      welcomeNight: 'Working late, {firstName}?',
      welcomeSubtitles: [
        'Your furnaces are waiting — let\'s make today a great run.',
        'Time to fire up the day — your processes are ready.',
        'Let\'s ensure every job runs to spec today.',
        'Monitor your sites, track your jobs, and stay in control.',
        'All your heat treatment operations, right at your fingertips.',
        'Your overview of every site, every furnace, every job.',
        'What\'s running today? Head to the App Overview to find out.',
        'Stay on top of your loads and keep quality in check.',
        'Check in on your furnaces and keep your processes on track.',
      ],
    },
    appOverview: {
      title: 'App Overview',
      showOnlyAvailableApps: 'Show only available apps',
      search: 'Search app',
    },
    releaseNotes: {
      title: 'Release notes',
    },
    releaseHighlights: {
      title: 'Release highlights',
    },
    userManual: {
      title: 'User Manual',
    },
    productionStatisticsDetails: {
      panelSettings: {
        header: 'Panel Settings',
        helpText: 'Choose a title for your panel and select the KPIs that shall be displayed. The type of chart can be chosen on the panel.',
        labelTitle: 'Title',
        labelSize: 'Size',
        labelType: 'Type',
        labelKpiSelection: 'KPI Selection',
      },
      size: {
        medium: 'Medium',
        large: 'Large',
      },
      type: {
        table: 'Table',
        totals: 'Totals',
        totalDistribution: 'Total Distribution',
        childrenTotals: 'Children Totals',
        childrenDistribution: 'Children Distribution',
        childrenComparison: 'Children Comparison',
      },
    },
    assetManagementEquipmentSensorMapping: {
      mappedSignalTitle: 'Mapped signal',
    },
    assetManagement: {
      overview: {
        title: 'Asset overview',
        tableHeader: 'Asset tree',
        filter: {
          name: 'Filter by name',
        },
      },
      equipmentTab: {
        [ERouteName.ASSET_MANAGEMENT_ALARM_MANAGEMENT]: 'Alarm Management',
        [ERouteName.ASSET_MANAGEMENT_CAMERAS]: 'Cameras',
        [ERouteName.ASSET_MANAGEMENT_DATA_SOURCES]: 'Data Sources',
        [ERouteName.ASSET_MANAGEMENT_DOCUMENT]: 'Documents',
        [ERouteName.ASSET_MANAGEMENT_MAIN]: 'Overview',
        [ERouteName.ASSET_MANAGEMENT_PARAMETER]: 'Parameters',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_MAPPING]: 'Sensor Mapping',
        [ERouteName.ASSET_MANAGEMENT_SENSOR_SETTINGS]: 'Sensor Settings',
      },
    },
    pt9800: {
      common: {
        id: 'ID',
        name: 'Name',
        description: 'Description',
        modifiedBy: 'Modified by',
        modifiedOn: 'Modified on',
        number: 'Number',
        comment: 'Comment',
      },
      confirmDeleteHeader: 'Delete {0} "{1}"?',
      error: {
        create: 'Failed to create {0}',
        update: 'Failed to update {0}',
        delete: 'Failed to delete {0}',
        clone: 'Failed to clone {0}',
      },
      form: {
        header: {
          create: 'Create {0}',
          edit: 'Edit {0}',
          delete: 'Delete {0}',
        },
      },
      entryType: {
        alarm: 'Alarm',
        application: 'Application',
        applicationGroup: 'Application Group',
        box: 'Box',
        clientAccess: 'Client Access',
        color: 'Color',
        terminal: 'Terminal',
        terminalType: 'Terminal Type',
        user: 'User',
        userAccess: 'User Access',
        userGroup: 'User Group',
        backgroundProcess: 'Background Process',
        tasks: 'Tasks',
        workplace: 'Workplace',
        workplaceType: 'Workplace Type',
        operation: 'Operation',
      },
      backgroundProcess: {
        order: 'Order',
        parameter: 'Parameter',
        active: 'Active',
        activeTime: 'Active Time',
        pid: 'PID',
        header: 'Background Process',
      },
      operation: {
        header: 'Operation',
        property: 'Property',
        operationType: 'Operation type',
        type: 'Type',
        mode: 'Mode',
        order: 'Order',
        peProcessType: 'PE process type',
        sortingNo: 'Sorting no',
        automatingPlanningMode: 'Automat planning mode',
        relevantForProductionStatistics: 'Relevant for production statistics',
        relevantForStat: 'Relevant for stat',
        selectProcess: 'Select process',
        selectPart: 'Select part',
        sapNo: 'Sap no.',
        workplaceName: 'Workplace/ WP-groups',
        placeDescription: 'Place description',
      },
      tasks: {
        header: 'Tasks administration',
        active: 'Active',
        type: 'Type',
        command1: 'Command 1',
        command2: 'Command 2',
        command3: 'Command 3',
        command4: 'Command 4',
        command5: 'Command 5',
        command6: 'Command 6',
        command7: 'Command 7',
        command8: 'Command 8',
        command9: 'Command 9',
        command10: 'Command 10',
        startHour: 'Start Hour',
        dateLastActive: 'Date last active',
        intervalInMin: 'Interval in min [min]',
      },
      workplace: {
        logicTypes: 'Logic Types',
        header: 'Workplace',
        inventoryNumber: 'Equipment number',
        belongsTo: 'Belongs to workplace id',
        ip: 'IP address',
        mGuardIp: 'MGuard IP',
        device: 'Device',
        telegramSplit: 'RFC telegram split',
        deactivateOperations: 'Deactivate operations',
        aliveBitErrorNum: 'Alive bit error number',
        logicType: 'Logic type',
        recipePrefix: 'Recipe prefix',
        chamberId: 'Chamber id',
        sharesController: 'Shares controller/copy of chamber-id',
        mainUseMode: 'Main use mode',
        workplaceType: 'Workplace type',
        isActive: 'Is active',
        importantSettings: 'Important settings',
      },
      alarm: {
        alarmAdministration: 'Alarm',
        alarm: 'Alarm',
        alarmText: 'Alarm Text',
        causesTestingLoad: 'Causes a testing load',
        onlyForChamber: 'Only for chamber',
        security: 'Security',
        color: 'Color',
        errorType: 'Error type',
        outputType: 'Output Type',
        alarmGroup: 'Alarm-Group',
      },
      application: {
        header: 'Application',
        command: 'Command',
        groupId: 'Group',
        iconGifName: 'Icon-Gif-Name',
        furnacePicture: 'Furnace picture',
        order: 'Order',
        parameter: 'Parameter',
        tooltip: 'Tooltip',
        typeId: 'Type',
      },
      applicationGroup: {
        header: 'Application Group',
      },
      systemData: {
        active: 'Active',
        addresseeMobilePhoneNo: 'Addressee mobile phone no',
        boxHeader: 'Box Administration',
        colorHeader: 'Color Administration',
        colorValue: 'Color value',
        pin: 'PIN',
        serialInterface: 'Serial interface',
        smsHeader: 'SMS',
        testMessage: 'Test message (1=YES)',
        used: 'Used',
      },
      terminal: {
        header: 'Terminal',
        id: 'Id',
        clientType: 'Terminal Type',
        name: 'Name',
        osType: 'Operating System',
        remark: 'Comment',
        address: 'Address',
        description: 'Description',
        location: 'Location',
        valueLong2: 'INT Value 2',
        valueLong3: 'INT Value 3',
        valueLong4: 'INT Value 4',
        valueLong5: 'INT Value 5',
        valueChar1: 'Character Value 1',
        valueChar2: 'Character Value 2',
        actUserName: 'Current user',
        actLoginTime: 'Logged in since',
        terminalNo: 'Terminal-No',
        lastLoginTime: 'Last Login-Time:',
        lastUserName: 'Last User',
        resolution: 'Screen-Resolution',
        permission: 'Permission',
        rightsToTerminals: 'Rights to Terminals',
      },
      terminalType: {
        header: 'Terminal Type',
      },
      user: {
        header: 'User',
        accessLevel: 'User Access Level',
        comment: 'Comment',
        costCenter: 'Cost Center',
        employeeNumber: 'Employee Number',
        shortName: 'Short Name',
        firstName: 'First Name',
        function: 'Function',
        lastName: 'Last Name',
        language: 'Language',
        loginTimeInMin: 'Login-Time in min.',
        maintenancePopups: 'Maintenance Plan Popups',
        password: 'Password',
        plant: 'Plant',
        playAlarmSound: 'Play alarm sound on Alarms',
        userGroup: 'User Group',
      },
      userAccess: {
        header: 'User Access',
        delete: 'Delete',
        newEntry: 'New Entry',
        print: 'Print',
        run: 'Run',
        save: 'Save',
        special1: 'Special-1',
        startRecipeProcessFlow: 'Start Recipe/Process Flow',
        modifyRecipeProcessFlow: 'Modify Recipe/Process Flow',
        startCorrectionRecipe: 'Start Correction Recipe',
        stopRecipeProcessFlow: 'Stop Recipe/Process Flow',
        selectUser: 'Select user',
      },
      userGroup: {
        header: 'User group',
      },
      workplaceType: {
        header: 'Workplace Type',
        prio: 'Prio',
      },
    },
    login: {
      title: 'Login',
    },
    anomalyDetectionJobFeatureDetail: {
      tab: {
        observationTable: 'Observations',
        rootCauseTable: 'Root Causes',
      },
    },
    anomalyDetectionEquipmentSettings: {
      title: 'Settings',
      tab: {
        counterActionTable: 'Counter Actions',
        rootCauseGroupTable: 'Root Causes Group',
        rootCauseTable: 'Root Causes',
        jobFeatureTable: 'Job Features',
      },
    },
    anomalyDetectionEquipment: {
      tab: {
        rootCauseTable: 'Possible Root causes',
        jobFeatureTable: 'Observations grouped by Job Features',
      },
      jobFeatureTable: {
        header: {
          jobFeatureName: 'Job Feature',
          observationCount: 'Observations count',
          rootCauseCount: 'Root Cause count',
        },
      },
      observationTypeByJobFeatureTable: {
        header: {
          observationTypeName: 'Observation type name',
          observationCount: 'Observations count',
          rootCauseIdList: 'Root cause count',
        },
      },
    },
    quality: {
      header: {
        title: 'Quality',
        createReport: 'Create report',
      },
    },
    auditResult: {
      asset: 'Asset',
      userOverruledReason: 'Reason for user overrule',
      duration: 'Duration',
      endTime: 'End time',
      headerTitle: 'Audit report',
      loadNo: 'Load No',
      location: 'Location',
      rating: 'Rating',
      recipe: 'Recipe',
      reportNo: 'Report No.',
      reporter: 'Reporter',
      signature: {
        customer: 'Customer',
        engineering: 'Engineering',
        manufacturing: 'Manufacturing',
        material: 'Material',
        program: 'Program',
        quality: 'Quality',
      },
      startTime: 'Start time',
    },
    productionLogList: {
      compare: 'Compare',
      export: 'Export',
    },
  },
  recipeBuilder: {
    alarmDeactivated: 'Alarm deactivated',
    alarmDelay: 'Alarm delay',
    equi: 'Equi',
    highAlarmOffsets: 'HIGH Alarm offsets',
    highWarningOffsets: 'HIGH Warning offsets',
    lowAlarmOffsets: 'LOW Alarm offsets',
    lowWarningOffsets: 'LOW Warning offsets',
    referenceValue: 'Reference value',
    setAlarmLimit: 'Set alarm limit',
    setAlarmLimits: 'Set alarm limits',
    setValue: 'Set value',
  },
  recipeOnDemand: {
    chart: {
      gasCompositionLabel: {
        ammonia: 'Ammonia: {value} %',
        dissociatedAmmonia: 'Dissociated Ammonia: {value} %',
        nitrogen: 'Nitrogen: {value} %',
        carbonOxide: 'Carbon Dioxide: {value} %',
        carbonDioxide: 'Carbon Dioxide: {value} %',
      },
      processLabel: {
        knAct: 'Kn Act: {value}',
      },
    },
    compoundLayerThickness: 'Compound layer thickness',
    coreHardnessHV: 'Core Hardness (HV)',
    diffusionLayerThickness: 'Diffusion layer thickness',
    distance: 'Distance',
    effective: 'Effective',
    gasFlowChart: 'Gas-flow chart',
    hardness: 'Hardness',
    material: 'Material',
    nitridetHardness: 'Nitrided hardness depth',
    nitriding: 'Nitriding',
    nitrocarburizing: 'Nitrocarburizing',
    oxideLayerThickness: 'Oxide layer thickness',
    porosity: 'Porosity %',
    postoxidation: 'Postoxidation',
    processChart: 'Process chart',
    process: 'Process',
    profileChart: 'Profile chart',
    stepNumber: 'Step {number}',
    surfaceHardness: 'Surface hardness',
    unit: 'Unit',
    unitSystems: {
      imperial: 'Imperial',
      metric: 'Metric',
    },
    vickersHardness: 'Vickers hardness',
  },
  recipes: {
    carburizingDepth: generalTerm.carburizingDepth,
    endTemperature: 'End Temperature',
    grainSize: 'ASTM grain size number',
    nitridingDepth: generalTerm.nitridingDepth,
    quenchingIntensity: 'Quenching Intensity',
    representativeDiameter: 'Representative Diameter',
    requiredHardnessHV: 'Req.Hardness',
    noAccess: 'User has no access to {0}',
  },
  report: {
    approvals: 'Approvals',
    customer: 'Customer',
    engineering: 'Engineering',
    manufacturing: 'Manufacturing',
    materiel: 'Materiel',
    program: 'Program',
    quality: 'Quality',
  },
  simulator: {
    overwrite: 'Overwrite',
  },
  tableHeaders: {
    alarmType: 'Alarm type',
    avg: 'Avg {unit}',
    description: 'Description',
    max: 'Max {unit}',
    min: 'Min {unit}',
    name: 'Name',
    value: 'value',
    valueWhenDeclared: 'Value when triggered',
    timeOfDeclaration: 'Time of trigger',
  },
  tabs: {
    materials: 'Materials',
    materialsGroups: 'Material groups',
  },
  tiles: {
    userManagement: 'User Management',
    materials: 'Materials',
    part: 'Parts',
    loadingScheme: 'Loading schemes',
    productionLogs: 'Production Logs',
    customer: 'Customer',
  },
  titles: {
    alarms: 'Alarms',
    noData: 'No Data',
    settings: 'Settings',
    valuesAndCalculations: 'Values and Calculations',
  },
  type: {
    Visualisation,
  },
  upcPro: {
    analyzerReading: 'Analyzer Reading',
    convertsTo: 'converts to',
    endoGeneratorDataError: 'DATA INPUT ERROR:\nCO[%] + CO₂[%] + CH₄[%] + H₂[%] > 100 \nPLEASE CORRECT VALUES.',
    floMeterGlossary: 'Glossary of terms:\n\nSG1 = specific gravity of gas Flo-Meter is calibrated for\n\nSG2 = specific gravity of gas Flo-Meter to be used\n\nTemp.1 = temperature that Flo-Meter is calibrated for\n\nTemp.2 = temperature that Flo-Meter will be used for\n\nP1 = pressure that Flo-Meter is calibrated for\n\nP2 = pressure that Flo-Meter will be used with\n',
    inputGroup: 'Input',
    flows: 'Input flows',
    furnaceAtmosphere: 'Furnace atmosphere',
    imperialUnits: 'imperial',
    metricUnits: 'metric',
    potentials: 'Potentials',
    inputs: {
      CELSIUS_TO_FAHRENHEIT: 'Celsius to Fahrenheit',
      FAHRENHEIT_TO_CELSIUS: 'Fahrenheit to Celsius',
      FT3PH_TO_M3PH: 'cfh to m³/h',
      GPM_TO_LMIN: 'US gpm to l/min',
      HARDNESS_TABLE1: 'Rockwell C Range',
      HARDNESS_TABLE2: 'Rockwell B Range',
      HBS: {
        name: 'HBS',
        subtitle: 'Brinell Scale',
      },
      HBS2: {
        name: 'HBS',
        subtitle: 'Brinell Scale',
      },
      HBW: {
        name: 'HBW',
        subtitle: 'Brinell Scale',
      },
      HK: {
        name: 'HK',
        subtitle: 'Knoop Hardness',
      },
      HK2: {
        name: 'HK',
        subtitle: 'Knoop Hardness',
      },
      HR15N: {
        name: 'HR15N',
        subtitle: 'Rockwell 15-N Scale',
      },
      HR15T: {
        name: 'HR15T',
        subtitle: 'Rockwell 15-T Scale',
      },
      HR30N: {
        name: 'HR30N',
        subtitle: 'Rockwell 30-N Scale',
      },
      HR30T: {
        name: 'HR30T',
        subtitle: 'Rockwell 30-T Scale',
      },
      HR45N: {
        name: 'HR45N',
        subtitle: 'Rockwell 45-N Scale',
      },
      HR45T: {
        name: 'HR45T',
        subtitle: 'Rockwell 45-T Scale',
      },
      HRA: {
        name: 'HRA',
        subtitle: 'Rockwell A Scale',
      },
      HRA2: {
        name: 'HRA',
        subtitle: 'Rockwell A Scale',
      },
      HRB: {
        name: 'HRB',
        subtitle: 'Rockwell B Hardness',
      },
      HRC: {
        name: 'HRC',
        subtitle: 'Rockwell C Hardness',
      },
      HRD: {
        name: 'HRD',
        subtitle: 'Rockwell D Scale',
      },
      HRF: {
        name: 'HRF',
        subtitle: 'Rockwell F Hardness',
      },
      HSC: {
        name: 'HSc',
        subtitle: 'HSc Scleroscope Hardness',
      },
      HV: {
        name: 'HV',
        subtitle: 'Vickers Hardness',
      },
      HV2: {
        name: 'HV',
        subtitle: 'Vickers Hardness',
      },
      LMIN_TO_GPM: 'l/min to US gpm',
      M3PH_TO_FT3PH: 'm³/h to cfh',
      ac: 'ac',
      actualScale: 'Actual Scale',
      aisi: 'AISI',
      al: 'Al',
      alloyFactor: 'Alloy Factor',
      baseC: 'Base C',
      c: 'C',
      carbonPotential: 'Carbon Potential',
      cch4: 'C + CH₄',
      ccontent: 'C-Content',
      ch3oh: 'CH₃OH',
      ch4: 'CH₄',
      ch4Factor: 'CH₄ Factor',
      co: 'CO',
      co2: 'CO₂',
      coTotalFlow: 'CO/Total Flow',
      coco2: 'CO/CO₂',
      coldJunctionTemp: 'Cold-Junction Temp.',
      cpatm: 'CPatm',
      cr: 'Cr',
      cu: 'Cu',
      dewPoint: 'Dew Point',
      din: 'DIN',
      dissBurette: 'Diss. Burette',
      dissDegree: 'Diss. Degree',
      dnh3: 'D-NH₃',
      dpt: 'DPT',
      emf: 'EMF',
      floMeterScale: 'Flo-Meter Scale',
      flow: 'Flow',
      gas: 'Gas',
      h2: 'H₂',
      h2n2: 'H₂:N₂',
      h2o: 'H₂O',
      hardness: 'Hardness',
      kcb: 'Kᴄʙ',
      kh: 'Kʜ',
      kn: 'Kɴ',
      ko: 'Kᴏ',
      mn: 'Mn',
      mo: 'Mo',
      mv: 'mV',
      n2: 'N₂',
      n2Percentage: 'N₂',
      n2ch3oh: 'N₂/CH₃OH',
      nc2: 'NC₂',
      nh3: 'NH₃',
      ni: 'Ni',
      o2: 'O₂',
      o2Percentage: 'O₂',
      o2Probe: 'O₂ Probe',
      others: 'Others',
      p1: 'P1',
      p2: 'P2',
      po2: 'pO₂',
      preload: 'Preload',
      pt10: {
        description: '(Pt10) Platinum RTD with Nominal Resistance of 10 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt10) Platinum RTD',
      },
      pt100: {
        description: '(Pt100) Platinum RTD with Nominal Resistance of 100 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt100) Platinum RTD',
      },
      pt1000: {
        description: '(Pt1000) Platinum RTD with Nominal Resistance of 1000 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt1000) Platinum RTD',
      },
      pt500: {
        description: '(Pt500) Platinum RTD with Nominal Resistance of 500 Ohms at 0°C (32°F).\nRange: -200°C - 850°C (-328°F - 1562°F) as per IEC 751 / DIN EN 60751.',
        name: '(Pt500) Platinum RTD',
      },
      resistance: 'Resistance',
      rg: 'RG',
      rl: 'RL',
      scaleFactor: 'Scale Factor',
      sg1: 'SG1',
      sg2: 'SG2',
      si: 'Si',
      sootLimit: 'Soot Limit',
      tc_b: {
        description: 'Plus:Platinum 30% Rhodium(Pt30Rh)\nMinus:Platinum 6% Rhodium (Pt6Rh)\nRange: -50°C-1820°C (-58°F-3308°F)\nColors according to ANSI 96.6.\nAccording to NFC 42-324, colors are yellow and gray',
        name: 'Type B = Pt30Rh-Pt6Rh',
      },
      tc_e: {
        description: 'Plus:Nickel-Chromium (NiCr), also\nknown as Chromel\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -270°C-1000°C (-454°F-1832°F)\nColors according to DIN EN 60584-1',
        name: 'Type E = NiCr-CuNi',
      },
      tc_j: {
        description: 'Plus:Copper(Cu), Copper Color\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -270°C-400°C (-454°F-752°F)\nColors according to DIN EN 60584-1',
        name: 'Type J = Fe-CuNi',
      },
      tc_k: {
        description: 'Plus:Nickel-Chromium (NiCr), also\nknown as Chromel\nMinus:Nickel Aluminum (NiAl) also\nknown as Alumel, magnetic wire\nRange: -270°C-1372°C (-454°F-2502°F)\nColors according to DIN EN 60584-1',
        name: 'Type K = NiCr-Ni',
      },
      tc_l: {
        description: 'Plus:Iron (Fe), magnetic wire\nMinus:Copper-Nicker(CuNi), also\nknown as Constantan\nRange: 0°C-900°C (32°F-1652°F)\nColors according to DIN EN 43714',
        name: 'Type L = Fe-CuNi',
      },
      tc_n: {
        description: 'Plus:Nickel-Chromium-Silicon (NiCrSi),\nalso known as Nicrosil\nMinus:Nickel-Silicon (NiSi), also\nknown as Nisil\nRange: -270°C-1300°C (-454°F-2372°F)\nColors according to DIN EN 60584-1',
        name: 'Type N = NiCrSi-NiSi',
      },
      tc_r: {
        description: 'Plus:Platinum 13% Rhodium (Pt13Rh)\nMinus:Platinum(Pt), minus wire softer\nthan plus wire\nRange: -50°C-1768°C (-58°F-3214°F)\nColors according to DIN EN 60584-1',
        name: 'Type R = Pt13Rh-Pt',
      },
      tc_s: {
        description: 'Plus:Platinum 10% Rhodium (Pt10Rh)\nMinus:Platinum(Pt), minus wire softer\nthan plus wire\nRange: -50°C-1768°C (-58°F-3214°F)\nColors according to DIN EN 60584-1',
        name: 'Type S = Pt10Rh-Pt',
      },
      tc_t: {
        description: 'Plus:Iron (Fe), magnetic wire\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: -210°C-1200°C (-346°F-2192°F)\nColors according to DIN EN 60584-1',
        name: 'Type T = Cu-CuNi',
      },
      tc_u: {
        description: 'Plus:Copper(Cu), Copper Color\nMinus:Copper-Nickel (CuNi), also\nknown as Constantan\nRange: 0°C-600°C (32°F-1112°F)\nColors according to DIN EN 43714',
        name: 'Type U = Cu-CuNi',
      },
      temp1: 'Temp.1',
      temp2: 'Temp.2',
      temperature: 'Temperature',
      total: 'Total',
      totalFlow: 'Total Flow',
      v: 'V',
      voltage: 'Voltage',
      weight1: 'Weight₁',
      weight2: 'Weight₂',
    },
    mustBeInteger: 'Must be integer',
    outOfRange: 'Out of Range',
    range: 'Range',
    units: 'Units',
  },
  validation: {
    required: 'Required',
    tooBigFile: 'Files size should be less than 50MB',
    // new
  },
  util: {
    schaefflerChart: {
      CrEquivalent: 'Cr-Equivalent',
      NiEquivalent: 'Ni-Equivalent',
    },
  },
  useImportJsonValidated: {
    unknownFileValidationError: 'Uploaded file could not be validated. Data is not as expected.',
  },
  tenantSettings: {
    dataSources: {
      headline: 'Data sources',
      subheadline: 'This page is used to manage Qmulus data sources.',
    },
    license: {
      headline: 'License key',
      subheadline: 'This page allows to review and update the license key of Qmulus.',
    },
    auditTrail: {
      headline: 'Audit Trail',
      subheadline: 'The audit trail records every change made in the application and assigns it to the responsible user, ensuring complete traceability of all activities.',
    },
  },
  unitSystem,
  ...getComponentScopedTranslationMap('page', 'en'),
  ...getComponentScopedTranslationMap('components', 'en'),
  ...getComponentScopedTranslationMap('pt9800', 'en'),
};
